/**
 * OIDC Configuration Utilities
 *
 * Provides configuration loading and URL construction for Keycloak OIDC.
 * Configuration values are loaded from environment variables.
 *
 * @module @project-x/auth/oidc/config
 */

import type { OIDCConfig } from "./types";
import { getRuntimeEnvValue } from "../runtime-env";

/**
 * Environment variable names for OIDC configuration.
 */
export const ENV_VARS = {
  /** Keycloak base URL (e.g., 'https://auth.gruv.fm') */
  KEYCLOAK_URL: "NEXT_PUBLIC_KEYCLOAK_URL",
  /** Keycloak realm name (e.g., 'gruv') */
  KEYCLOAK_REALM: "NEXT_PUBLIC_KEYCLOAK_REALM",
  /** OIDC client ID (e.g., 'consumer-web') */
  KEYCLOAK_CLIENT_ID: "NEXT_PUBLIC_KEYCLOAK_CLIENT_ID",
  /** Public host for redirect URIs */
  PUBLIC_HOST: "NEXT_PUBLIC_PUBLIC_HOST",
  /** Public scheme (http or https) */
  PUBLIC_SCHEME: "NEXT_PUBLIC_PUBLIC_SCHEME",
  /** Users service URL for /users/ensure */
  USERS_URL: "NEXT_PUBLIC_USERS_URL",
  /** Dedicated Keycloak realm for the admin UI (isolated from consumer/manager/validator) */
  KEYCLOAK_ADMIN_REALM: "NEXT_PUBLIC_KEYCLOAK_ADMIN_REALM",
} as const;

/**
 * Default configuration values.
 */
const DEFAULTS = {
  KEYCLOAK_URL: "https://auth.gruv.fm",
  KEYCLOAK_REALM: "gruv",
  // Prod default preserved exactly as before (zero prod risk); dev sets
  // NEXT_PUBLIC_KEYCLOAK_ADMIN_REALM explicitly to its own isolated realm.
  KEYCLOAK_ADMIN_REALM: "gruv-admin",
  PUBLIC_HOST: "gruv.fm",
  PUBLIC_SCHEME: "https",
  SCOPES: ["openid", "email", "profile"],
} as const;

/**
 * Gets an environment variable value with fallback.
 *
 * @param name - Environment variable name
 * @param fallback - Fallback value if not defined
 * @returns Environment variable value or fallback
 */
function getEnvVar(name: string, fallback: string): string {
  // Client-side: prefer the request-time value injected by <RuntimeEnvScript />
  // (see ../runtime-env.ts) so one built image can serve prod and dev with
  // different hosts/realms — NEXT_PUBLIC_* read directly here would otherwise
  // be inlined at build time by webpack and frozen to whatever env built the image.
  if (typeof window !== "undefined") {
    const runtimeValue = getRuntimeEnvValue(name);
    if (runtimeValue) {
      return runtimeValue;
    }
  }
  // Server-side (or client fallback for local `next dev` without the script,
  // and for keys outside RUNTIME_PUBLIC_ENV_KEYS): process.env.NEXT_PUBLIC_*
  // is always the live container value when read in server code.
  if (typeof process !== "undefined" && process.env?.[name]) {
    return process.env[name] as string;
  }
  return fallback;
}

/**
 * Gets the Keycloak base URL from environment variables.
 *
 * @returns Keycloak base URL (e.g., 'https://auth.gruv.fm')
 */
export function getKeycloakUrl(): string {
  return getEnvVar(ENV_VARS.KEYCLOAK_URL, DEFAULTS.KEYCLOAK_URL).replace(/\/$/, "");
}

/**
 * Gets the Keycloak URL for server-side use, preferring the internal cluster URL.
 *
 * Reads KEYCLOAK_INTERNAL_URL (a runtime env var, not baked in at build time like
 * NEXT_PUBLIC_*) so that Next.js SSR routes call Keycloak over the internal K8s
 * network instead of going through the external load balancer.
 *
 * Falls back to the public URL when KEYCLOAK_INTERNAL_URL is not set (e.g., local dev).
 *
 * @returns Internal Keycloak URL for server-side calls
 */
export function getServerKeycloakUrl(): string {
  if (typeof process !== "undefined" && process.env?.KEYCLOAK_INTERNAL_URL) {
    return (process.env.KEYCLOAK_INTERNAL_URL as string).replace(/\/$/, "");
  }
  return getKeycloakUrl();
}

/**
 * Gets the Keycloak realm name from environment variables.
 *
 * @returns Keycloak realm name (e.g., 'gruv')
 */
export function getKeycloakRealm(): string {
  return getEnvVar(ENV_VARS.KEYCLOAK_REALM, DEFAULTS.KEYCLOAK_REALM);
}

/**
 * Gets the dedicated admin-UI Keycloak realm name from environment variables.
 *
 * Isolated from the primary realm on purpose: a compromised consumer/manager
 * credential can never reach the admin UI, in any environment. Defaults to
 * "gruv-admin" (prod's existing realm) so this is a zero-risk addition; dev
 * sets NEXT_PUBLIC_KEYCLOAK_ADMIN_REALM to its own dedicated dev admin realm.
 *
 * @returns Admin Keycloak realm name (e.g., 'gruv-admin')
 */
export function getKeycloakAdminRealm(): string {
  return getEnvVar(ENV_VARS.KEYCLOAK_ADMIN_REALM, DEFAULTS.KEYCLOAK_ADMIN_REALM);
}

/**
 * Gets the OIDC client ID from environment variables.
 *
 * @returns OIDC client ID (e.g., 'consumer-web')
 */
export function getClientId(): string {
  return getEnvVar(ENV_VARS.KEYCLOAK_CLIENT_ID, "consumer-web");
}

/**
 * Gets the public scheme (http or https) from environment variables.
 *
 * @returns Public scheme
 */
export function getPublicScheme(): string {
  return getEnvVar(ENV_VARS.PUBLIC_SCHEME, DEFAULTS.PUBLIC_SCHEME);
}

/**
 * Gets the public host from environment variables.
 *
 * @returns Public host (e.g., 'gruv.fm')
 */
export function getPublicHost(): string {
  return getEnvVar(ENV_VARS.PUBLIC_HOST, DEFAULTS.PUBLIC_HOST);
}

/**
 * Gets the users service URL from environment variables (client-safe, public URL).
 *
 * @returns Users service URL (e.g., 'https://api.gruv.fm/users')
 */
export function getUsersUrl(): string {
  const scheme = getPublicScheme();
  const host = getPublicHost();
  return getEnvVar(ENV_VARS.USERS_URL, `${scheme}://api.${host}/users`).replace(/\/$/, "");
}

/**
 * Gets the users service URL for server-side use, preferring the internal cluster URL.
 *
 * Reads USERS_URL (a runtime env var, not baked in at build time like NEXT_PUBLIC_*)
 * so that Next.js API routes call the users service over the internal K8s network
 * instead of hairpinning through the external load balancer and Cloud Armor.
 *
 * Falls back to the public URL when USERS_URL is not set (e.g., local dev).
 *
 * @returns Internal users service URL for server-side calls
 */
export function getServerUsersUrl(): string {
  if (typeof process !== "undefined" && process.env?.USERS_URL) {
    const trimmed = (process.env.USERS_URL as string).trim();
    if (trimmed.length > 0) {
      return trimmed.replace(/\/$/, "");
    }
  }
  return getUsersUrl();
}

/**
 * Constructs the Keycloak realm URL.
 *
 * @param keycloakUrl - Keycloak base URL
 * @param realm - Realm name
 * @returns Realm URL (e.g., 'https://auth.gruv.fm/realms/gruv')
 */
export function getRealmUrl(keycloakUrl: string = getKeycloakUrl(), realm: string = getKeycloakRealm()): string {
  return `${keycloakUrl}/realms/${realm}`;
}

/**
 * Constructs the OIDC authorization endpoint URL.
 *
 * @param realmUrl - Keycloak realm URL
 * @returns Authorization endpoint URL
 */
export function getAuthorizationEndpoint(realmUrl: string = getRealmUrl()): string {
  return `${realmUrl}/protocol/openid-connect/auth`;
}

/**
 * Constructs the OIDC token endpoint URL.
 *
 * @param realmUrl - Keycloak realm URL
 * @returns Token endpoint URL
 */
export function getTokenEndpoint(realmUrl: string = getRealmUrl()): string {
  return `${realmUrl}/protocol/openid-connect/token`;
}

/**
 * Constructs the OIDC logout endpoint URL.
 *
 * @param realmUrl - Keycloak realm URL
 * @returns Logout endpoint URL
 */
export function getLogoutEndpoint(realmUrl: string = getRealmUrl()): string {
  return `${realmUrl}/protocol/openid-connect/logout`;
}

/**
 * Constructs the JWKS endpoint URL.
 *
 * @param realmUrl - Keycloak realm URL
 * @returns JWKS endpoint URL
 */
export function getJwksEndpoint(realmUrl: string = getRealmUrl()): string {
  return `${realmUrl}/protocol/openid-connect/certs`;
}

/**
 * Constructs the userinfo endpoint URL.
 *
 * @param realmUrl - Keycloak realm URL
 * @returns Userinfo endpoint URL
 */
export function getUserinfoEndpoint(realmUrl: string = getRealmUrl()): string {
  return `${realmUrl}/protocol/openid-connect/userinfo`;
}

/**
 * Creates an OIDC configuration object from environment variables.
 *
 * @param clientId - Optional client ID override
 * @param redirectUri - Optional redirect URI override
 * @param scopes - Optional scopes override
 * @returns Complete OIDC configuration
 */
export function createOIDCConfig(
  clientId?: string,
  redirectUri?: string,
  scopes?: string[],
): OIDCConfig {
  const realmUrl = getRealmUrl();
  const scheme = getPublicScheme();
  const host = getPublicHost();
  const resolvedClientId = clientId ?? getClientId();

  // Determine base URL from client ID if redirect URI not provided
  let baseUrl: string;
  switch (resolvedClientId) {
    case "consumer-web":
      baseUrl = `${scheme}://${host}`;
      break;
    case "manager-web":
      baseUrl = `${scheme}://manager.${host}`;
      break;
    case "admin-web":
      baseUrl = `${scheme}://admin.${host}`;
      break;
    case "validator-web":
      baseUrl = `${scheme}://scan.${host}`;
      break;
    default:
      baseUrl = `${scheme}://${host}`;
  }

  return {
    authorizationEndpoint: getAuthorizationEndpoint(realmUrl),
    tokenEndpoint: getTokenEndpoint(realmUrl),
    logoutEndpoint: getLogoutEndpoint(realmUrl),
    jwksEndpoint: getJwksEndpoint(realmUrl),
    userinfoEndpoint: getUserinfoEndpoint(realmUrl),
    clientId: resolvedClientId,
    redirectUri: redirectUri ?? `${baseUrl}/auth/callback`,
    postLogoutRedirectUri: `${baseUrl}/auth/logout/callback`,
    scopes: scopes ?? [...DEFAULTS.SCOPES],
  };
}

/**
 * Client ID constants for each frontend application.
 */
export const CLIENT_IDS = {
  CONSUMER: "consumer-web",
  VALIDATOR: "validator-web",
  MANAGER: "manager-web",
  ADMIN: "admin-web",
} as const;

export type ClientId = (typeof CLIENT_IDS)[keyof typeof CLIENT_IDS];
